import React from 'react';

// ListPage - the ONE list-page layout (UI/UX hand-off W-H5, W-M9, W-M20, W-iP6).
//
//   <ListPage>
//     <PageHeader ... />            fixed: title + actions
//     <FiltersCard ... />           fixed: filters (collapsed by default on a tablet)
//     <ListPage.Body>               fills ALL the remaining height
//       <DataTable fill ... />      ...and the table inside it is the one thing that scrolls
//     </ListPage.Body>
//     <ServerPagination ... />      fixed: pinned under the table
//   </ListPage>
//
// Why. List tables used to sit in fixed-height inner boxes (1-3 rows visible at laptop height,
// 1 row on PCR), while the page, the table and the modals each scrolled - three nested vertical
// scroll areas. Here the page is a fixed-height column and the table region takes whatever is
// left, so there is exactly ONE vertical scroll area: the table's own scroll box, whose sticky
// header attaches to the element that actually scrolls and whose horizontal scrollbar sits at the
// bottom of the table.
//
// Fallback. The body has a minimum height (`md:min-h-[16rem]`), so on a very short window with
// every filter open the page itself scrolls rather than squeezing the table to nothing. On a
// phone (< md) tables render as a card list and the page simply scrolls as one column.
//
// `scrollbar-gutter: stable` reserves the page scrollbar's width, so the header buttons no longer
// shift ~12px sideways when a scrollbar appears.

type DivProps = React.HTMLAttributes<HTMLDivElement>;

export interface ListPageProps extends DivProps {
  /** Extra classes for the page container (e.g. a legacy page-specific hook class). */
  className?: string;
}

interface ListPageType extends React.FC<ListPageProps> {
  Body: React.ForwardRefExoticComponent<DivProps & React.RefAttributes<HTMLDivElement>>;
}

/** The body's minimum height (md:min-h-[16rem]). */
const BODY_MIN_PX = 256;

/**
 * V2-028 / LAY-03: exactly one vertical scroll area. When the fixed parts (header, filters,
 * totals, pagination) leave the body less than its minimum, the page has to scroll – and then the
 * table must not scroll as well. The space is measured from the fixed parts only, so switching
 * modes never changes the measurement (no flicker between the two).
 */
function usePageScrollMode(ref: React.RefObject<HTMLDivElement>): boolean {
  const [pageScrolls, setPageScrolls] = React.useState(false);
  React.useLayoutEffect(() => {
    const page = ref.current;
    if (!page || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const style = window.getComputedStyle(page);
      const padding = parseFloat(style.paddingTop || '0') + parseFloat(style.paddingBottom || '0');
      let fixed = 0;
      let hasBody = false;
      for (const child of Array.from(page.children) as HTMLElement[]) {
        if (child.dataset.slot === 'list-page-body') {
          hasBody = true;
          continue;
        }
        const cs = window.getComputedStyle(child);
        fixed += child.offsetHeight + parseFloat(cs.marginTop || '0') + parseFloat(cs.marginBottom || '0');
      }
      setPageScrolls(hasBody && page.clientHeight - padding - fixed < BODY_MIN_PX);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(page);
    Array.from(page.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [ref]);
  return pageScrolls;
}

export const ListPage = (({ className = '', children, ...rest }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const pageScrolls = usePageScrollMode(ref);
  return (
    <div
      ref={ref}
      data-slot="list-page"
      data-page-scrolls={pageScrolls || undefined}
      className={`wrap-list-page tw-flex tw-h-full tw-min-h-0 tw-w-full tw-min-w-0 tw-flex-1 tw-flex-col tw-overflow-y-auto tw-overflow-x-hidden tw-p-3 md:tw-p-4 [&>*]:tw-shrink-0 ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}) as ListPageType;

// Body - the region that takes the height left under the header and filters. `!shrink` and
// `!grow` beat the page's `[&>*]:shrink-0` (which keeps header, filters and pagination at their
// natural height).
const ListPageBody = React.forwardRef<HTMLDivElement, DivProps>(({ className = '', children, ...rest }, ref) => (
  <div
    ref={ref}
    data-slot="list-page-body"
    className={`tw-flex tw-min-w-0 tw-flex-col md:!tw-shrink md:!tw-grow md:tw-basis-0 md:tw-min-h-[16rem] ${className}`}
    {...rest}
  >
    {children}
  </div>
));
ListPageBody.displayName = 'ListPage.Body';
ListPage.Body = ListPageBody;

/**
 * TableScroll - the single scroll box for a hand-written table (the big task tables, which do not
 * use DataTable). White framed surface; it scrolls on both axes so the sticky header (`<Table
 * stickyHeader>`) and pinned columns attach to it, and the horizontal scrollbar is at its bottom.
 * Put it inside `ListPage.Body`; it fills the body.
 */
export const TableScroll = React.forwardRef<HTMLDivElement, DivProps>(({ className = '', children, ...rest }, ref) => (
  <div
    ref={ref}
    data-slot="table-scroll"
    className={`wrap-table-scroll tw-relative tw-min-h-0 tw-min-w-0 tw-flex-1 tw-overflow-auto tw-overscroll-contain tw-rounded-lg tw-border tw-border-line-input tw-bg-surface ${className}`}
    {...rest}
  >
    {children}
  </div>
));
TableScroll.displayName = 'TableScroll';

export default ListPage;
